<template>
  <v-container
    class="view-home">
    <v-app-bar
      absolute>
      <label
        class="title text-center">
        Vivantes Bewerberportal
      </label>
      <v-text-field
        outlined
        dense
        placeholder="Suche nach Name ..."
        v-model="searchString"
      />
      <v-select
        v-model="activeSorterIndex"
        outlined
        dense
        :items="sorter"
        item-text="label"
        :item-value="item => sorter.indexOf(item)"
        label="Sortiert nach"
        />
      <v-spacer />
      <v-select
        v-model="selectedStatus"
        outlined
        dense
        :items="possibleFilterItems"
        @change="requestCandidates">
        <template slot="selection" slot-scope="data">
          <v-badge
            color="grey lighten-2 grey--text"
            inline
            :content="data.item.count || '0'">
            {{ data.item.label }}
          </v-badge>
        </template>
        <template slot="item" slot-scope="data">
          <v-badge
            color="grey lighten-2 grey--text"
            inline
            :content="data.item.count || '0'">
            {{ data.item.label }}
          </v-badge>
        </template>
      </v-select>
      <v-spacer />
      <v-btn
        depressed
        color="primary"
        :disabled="readOnlyMode"
        v-if="hasPermissionToCreateCandidate"
        @click="onClickCreateNew">
        Neu
      </v-btn>
    </v-app-bar>
    <v-progress-linear indeterminate v-if="isLoading"/>

    <vmnstr
      :elements="displayedCandidates"
      :threshold-ratio="0.2"
      :use-transform="true"
      :key-for-element="element => element.uuid">

      <FilterOverlay
        slot="accessory" />

      <Candidate
        slot-scope="{ element }"
        :candidate="element"
        @clicked="onClickCandidate" />

      <div
        v-if="!isLoading && (!displayedCandidates || !displayedCandidates.length)"
        class="no-result-message"
        slot="accessory">
        <v-icon
          x-large>
          mdi-emoticon-sad-outline
        </v-icon>
        <span
          v-if="candidates.length === 0">
          Diese Bewerberliste ist leer.
        </span>
        <span
          v-else>
          Die eingestellten Filter- und Suchparameter passen auf keinen Bewerber in dieser Liste.
        </span>
      </div>

    </vmnstr>

    <VDialogExt
      v-model="detailDialogActive"
      scrollable
      persistent
      max-width="640">
      <QuestionForm
        v-if="detailDialogActive"
        :candidate="selectedCandidate"
        @close="onCloseDetailDialog"/>
    </VDialogExt>
  </v-container>
</template>

<script>
import vmnstr from '@/plugins/vmnstr'
import QuestionForm from '@/components/QuestionForm'
import BL from '@/bl/bl'
import StatusBl from '@/bl/statusBl'
import FilterOverlay from './FilterOverlay'
import VDialogExt from '@/components/VDialogExt'
import Candidate from '../components/Candidate'
import CandidateBL from '@/bl/candidateBl'

export default {
  name: 'Home',

  data () {
    return {
      candidateFilterDialogActive: false,
      detailDialogActive: false,
      selectedCandidate: void 0,
      selectedStatus: StatusBl.getCandidateFilterOptions()[0],
      isLoading: false,
      searchString: '',
      sorter: [
        {
          key: 'created',
          label: 'Erstellungsdatum',
          fn: (a, b) => CandidateBL.getDateCreated(a) > CandidateBL.getDateCreated(b) ? 1 : -1
        },
        {
          key: 'qualification',
          label: 'Qualifikation',
          fn: (a, b) => {
            const qa = CandidateBL.getQualification(a)
            const qb = CandidateBL.getQualification(b)
            return qa ? (qa > qb ? 1 : -1) : 1
          }
        }
      ],
      activeSorterIndex: 0
    }
  },

  components: {
    Candidate,
    vmnstr,
    QuestionForm,
    FilterOverlay,
    VDialogExt
  },

  mounted () {
    window.$page = this
  },

  computed: {
    possibleFilterItems () {
      const filterItems = StatusBl.getCandidateFilterOptions()
      return filterItems.map(item => {
        let count = 0
        switch (item.status) {
          case 'new':
            count = this.$store.getters['getInboxCount']
            break
          case 'warteliste':
            count = this.$store.getters['getWartelisteCount']
            break
          case 'eingestellt':
            count = this.$store.getters['getEingestelltCount']
            break
          case 'abgesagt':
            count = this.$store.getters['getAbgesagtCount']
            break
          case 'verified':
            count = this.$store.getters['getVerifiedCount']
            break
          case 'all':
            count = this.$store.getters['getAllCount']
            break
          default:
            throw new Error('Unsupported Type')
        }
        item.count = count
        item.labelWithCount = item.label + ' (' + count + ')'
        return item
      })
    },

    activeSorter () {
      return this.sorter[this.activeSorterIndex]
    },

    displayedCandidates () {
      const filterNeedles = this.searchString ? this.searchString.toLowerCase().split(' ') : []

      const candidates = this.isLoading
        ? []
        : this.$store
          .getters['getDisplayedCandidates']
          .filter(candidate => {
            if (!this.searchString) {
              return true
            }
            const candidateName = CandidateBL.getName(candidate, candidate.answers).toLowerCase()
            return filterNeedles.every(needle => candidateName.includes(needle))
          })

      if (this.activeSorter && typeof this.activeSorter.fn === 'function') {
        candidates.sort(this.activeSorter.fn)
      }

      return candidates
    },

    candidates () {
      return BL.getCandidates()
    },

    hasPermissionToCreateCandidate () {
      return this.$store.getters['hasPermissionToCreateCandidate']
    },

    hasPermissionToViewCandidatesList () {
      return this.$store.getters['hasPermissionToViewCandidatesList']
    },

    readOnlyMode () {
      return this.$store.getters['readOnlyMode']
    }
  },

  methods: {
    onClickCreateNew () {
      this.selectedCandidate = void 0
      this.detailDialogActive = true
      this.$store.commit('clearCandidateUUID')
      this.$store.commit('generateCandidateUUID')
    },

    onClickCandidate (candidate) {
      this.selectedCandidate = candidate
      this.$store.commit('setCandidateUUID', candidate.uuid)
      this.detailDialogActive = true
    },

    onCloseDetailDialog () {
      this.detailDialogActive = false
      this.$store.commit('clearCandidateUUID')
    },

    requestCandidates (status) {
      this.$store.commit('setCurrentCandidateFilter', status)
      if (status === 'new') {
        status = undefined
      }
      this.isLoading = true
      Promise.all([
        BL.requestCandidates(status)
      ]).finally(() => {
        this.isLoading = false
      })
      BL.requestCandidateCount()
    }
  }
}
</script>

<style lang="scss">
  .view-home {
    $FILTER_WIDTH: 320px;
    $APP_BAR_HEIGHT: 64px;

    max-width: none;

    .v-app-bar {
      background-color: white !important;
      box-shadow: 0 16px 32px rgba(0,0,0,0.1);

      .v-toolbar__content {
        padding-left: 0;
        > label {
          flex: 0 0 $FILTER_WIDTH;
          margin-right: 0.75em;
        }
      }

      .v-select,
      .v-text-field {
        margin-right: 1em;

        .v-input__control {
          .v-input__slot {
            margin-bottom: 0;
            background-color: #f0f0f0;
          }
          fieldset {
            border-color: transparent !important;
          }
          .v-text-field__details {
            display: none;
          }
        }
      }

      .v-select {
        flex: 0 0 12em !important;
        font-weight: normal;
      }

      .v-text-field {
        flex: 1 1 100%;
      }
    }

    &,
    .vmnstr {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      padding: 0;

      .mnstr-accessory {
        top: $APP_BAR_HEIGHT !important;

        .v-card {
          width: $FILTER_WIDTH;
        }
      }
    }

    .vmnstr {
      .mnstr {
        padding-top: $APP_BAR_HEIGHT;

        .mnstr-accessory {
          height: calc(100% - #{$APP_BAR_HEIGHT});

          .no-result-message {
            position: absolute;
            left: 0;
            top: 0;
            width: 100vw;
            padding-left: $FILTER_WIDTH;
            height: 100%;
            pointer-events: none;

            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            span,
            .v-icon {
              color: #999;
            }

            span {
              margin-top: 1em;
            }
          }
        }
      }
    }

    .view-filter {
      background: #f9f9f9;
      padding: 2em;
      min-height: 100%;
    }

    .candidate-list-item {
      margin-left: $FILTER_WIDTH;
    }

    .v-progress-linear {
      top: 65px;
    }
  }
</style>
